import { useCallback, useEffect, useState } from "react";
import { ClipboardCheck, Cpu, Lock, Pencil, Loader2 } from "lucide-react";
import { ReviewModal } from "./ReviewModal";
import type { InitiativePrefill } from "./ReviewModal";
import { barColor, getReviewsForInitiative, loadReviewConfig } from "../reviews";
import type { Perspective, ReviewConfig, ReviewItem } from "../reviews";

interface Props {
  initiative: InitiativePrefill;
  getToken: () => Promise<string>;
  reviewerUpn: string;
  reviewerName: string;
  /** Only admins may open/edit reviews. */
  canReview?: boolean;
  /**
   * Fires after a review is successfully saved. Lets a parent refresh data
   * derived from the review — e.g. re-fetch the admin table's initiative rows
   * so their Business / Technical / Avg score columns reflect the new values.
   */
  onReviewSaved?: (saved: ReviewItem) => void;
}

/**
 * Renders the Business + Technical review buttons for one initiative.
 *
 * Gating rule (per the feature request): the Technical Review button is
 * locked until a Business score has been saved. Once either review
 * exists, its button switches to an "Update" affordance with a score
 * chip, and the modal preloads the saved answers for editing.
 */
export function InitiativeReviewActions({
  initiative,
  getToken,
  reviewerUpn,
  reviewerName,
  canReview = true,
  onReviewSaved,
}: Props) {
  const [business, setBusiness] = useState<ReviewItem | null>(null);
  const [technical, setTechnical] = useState<ReviewItem | null>(null);
  const [config, setConfig] = useState<ReviewConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<Perspective | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const [cfg, reviews] = await Promise.all([
        loadReviewConfig(token),
        getReviewsForInitiative(token, initiative.id),
      ]);
      setConfig(cfg);
      setBusiness(reviews.business);
      setTechnical(reviews.technical);
    } catch {
      // Non-fatal: leave buttons in their default (unreviewed) state.
    } finally {
      setLoading(false);
    }
  }, [getToken, initiative.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const businessScored = !!business && business.composite > 0;

  const onSaved = (saved: ReviewItem) => {
    if (saved.perspective === "Business") setBusiness(saved);
    else setTechnical(saved);
    setOpen(null);
    // Bubble up so the admin table (which owns the initiative rows) can
    // re-fetch and show the updated scores.
    onReviewSaved?.(saved);
  };

  if (!canReview) return null;

  return (
    <div className="rv-actions">
      {loading ? (
        <span className="rv-actions-loading">
          <Loader2 size={14} className="rv-spin" /> Loading reviews…
        </span>
      ) : (
        <>
          {/* Business Review */}
          <ReviewButton
            label="Business Review"
            icon={<ClipboardCheck size={15} />}
            review={business}
            onClick={() => setOpen("Business")}
          />

          {/* Technical Review — gated on a saved business score */}
          {businessScored ? (
            <ReviewButton
              label="Technical Review"
              icon={<Cpu size={15} />}
              review={technical}
              onClick={() => setOpen("Technical")}
            />
          ) : (
            <button className="rv-action rv-action--locked" type="button" disabled title="Save a Business score first">
              <Lock size={15} />
              Technical Review
              <span className="rv-action-hint">Locked</span>
            </button>
          )}
        </>
      )}

      {open && config ? (
        <ReviewModal
          initiative={initiative}
          perspective={open}
          existing={open === "Business" ? business : technical}
          config={config}
          getToken={getToken}
          reviewerUpn={reviewerUpn}
          reviewerName={reviewerName}
          onClose={() => setOpen(null)}
          onSaved={onSaved}
        />
      ) : null}
    </div>
  );
}

function ReviewButton({
  label,
  icon,
  review,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  review: ReviewItem | null;
  onClick: () => void;
}) {
  const scored = !!review && review.composite > 0;
  return (
    <button
      className={`rv-action ${scored ? "rv-action--done" : "rv-action--open"}`}
      type="button"
      onClick={onClick}
    >
      {scored ? <Pencil size={15} /> : icon}
      {label}
      {scored ? (
        <span className="rv-action-score" style={{ background: barColor(review!.composite) }}>
          {review!.composite.toFixed(2)}
        </span>
      ) : (
        <span className="rv-action-hint">Start</span>
      )}
    </button>
  );
}
